import PropTypes from 'prop-types';
import { useEffect, useState, useMemo, createRef } from 'react';
import { Trans, withTranslation } from 'react-i18next';

import { getEnv } from '@ctct/ctct-helpers';
import { trackAction } from '@ctct/distui-helpers';
import { Alert, Button, Dialog, CheckBox, UpsellCard, ThemeImage } from '@ctct/ui-react';

import { getBillingCustomerCurrency, getIsTrialer } from '../../api/DistUI';
import MyAccountApiLite from '../../api/MyAccountApiLite';
import { getStripeCustomer } from '../../api/StripeApi';
import AddOnIds from '../../constants/addOnIds';
import { getSupportPhoneNumber } from '../../utils/getSupportPhoneNumber';
import { observabilityAppService } from '../../utils/ObservabilityAppService';
import { transformCustomerInfo } from '../../utils/orderSummaryUtils';
import { formatCurrency } from '../../utils/pricingUtils';
import OrderSummary from '../OrderSummary';
import SMSTiersTable from '../SMSTiersTable';
import './_SMSTierDialogWithOrderSummary.scss';

const SMSTierDialogWithOrderSummaryV1 = ({ t, open, closeDialog, smsPurchasedCallback, closeOnSuccess }) => {
  const [smsSelection, setSmsSelection] = useState();
  const [SMSTiers, setSMSTiers] = useState([]);
  const [loadingSMSTiers, setLoadingSMSTiers] = useState(true);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState();
  const [overview, setOverview] = useState();
  const [paymentPlanGroupId, setPaymentPlanGroupId] = useState();
  const [planId, setPlanId] = useState();
  const [discountCode, setDiscountCode] = useState();
  const [appliedDiscountCode, setAppliedDiscountCode] = useState();
  const [discountError, setDiscountError] = useState();
  const [contactCount, setContactCount] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const [addSmsAddon, setAddSmsAddon] = useState(false);
  const [successfulPayment, setSuccessfulPayment] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [currency, setCurrency] = useState('USD');
  const [rating, setRating] = useState();
  const [isTrialAccount, setIsTrialAccount] = useState(null);
  const [smsBaseCost, setSmsBaseCost] = useState(null);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [promoEnabled, setPromoEnabled] = useState(false);
  const [isStripeAccount, setIsStripeAccount] = useState(false);
  const [stripePaymentToken, setStripePaymentToken] = useState(null);
  const alertRef = createRef();

  // Determine support phone number based on customer country
  const supportPhone = getSupportPhoneNumber(customer?.country);

  const {
    getBillingInfo,
    getPlanGroup,
    getPlanRating,
    payForPlan,
    getSMSTiers,
    getUserPlanOverview,
    getRMCValue,
    getBillingAccount,
  } = MyAccountApiLite;

  useEffect(() => {
    if (errorMessage && alertRef) {
      alertRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [errorMessage]);

  useEffect(() => {
    (async () => {
      if (open) {
        const isTrialer = await getIsTrialer();

        if (isTrialer) {
          setIsTrialAccount(true);
          window.location.href = `https://app${getEnv()}constantcontact.com/pages/myaccount/base/purchase/plans-and-pricing`;
        } else if (SMSTiers.length === 0) {
          try {
            trackAction({ action: 'stp-sms-sms-dialog-view' });
            setIsTrialAccount(false);

            const localCurrency = await getBillingCustomerCurrency();

            setCurrency(localCurrency);

            const [planGroupResult, result, { current_contacts: currentContactsStr }, billingAccount] =
              await Promise.all([
                getPlanGroup(localCurrency),
                getBillingInfo(),
                getUserPlanOverview(),
                getBillingAccount(),
              ]);
            const { paymentPlanId, currentPlanId } = planGroupResult;
            const { customer: customerLocal, overview: overviewLocal } = transformCustomerInfo(result, localCurrency);

            setCustomer(customerLocal);
            setOverview(overviewLocal);
            setPaymentPlanGroupId(paymentPlanId);
            setPlanId(currentPlanId);
            setPromoEnabled(await getRMCValue('SMS_INTRODUCTORY_PROMOTION_ENABLED'));

            const isStripe = billingAccount?.billingSystemVersion >= 4;

            setIsStripeAccount(isStripe);

            if (isStripe) {
              try {
                const stripeCustomer = await getStripeCustomer();
                const paymentToken = stripeCustomer?.paymentMethod?.id;

                if (paymentToken) {
                  setStripePaymentToken(paymentToken);
                }
              } catch (e) {
                observabilityAppService.notify(e, { message: 'Could not get stripe customer in SMS dialog V1' });
              }
            }

            const contactCountLocal = currentContactsStr ? parseInt(currentContactsStr.replace(/,/g, ''), 10) : 0;

            setContactCount(contactCountLocal);
            setLoadingSMSTiers(true);

            const priceTiers = await getSMSTiers(localCurrency, currentPlanId);

            setSMSTiers(priceTiers);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('exception getting billing information', e);
          } finally {
            setLoadingSMSTiers(false);
          }
        }
      }
    })();
  }, [open]);

  useEffect(() => {
    (async () => {
      if (addSmsAddon && rating) {
        try {
          setErrorMessage(null);

          const { stripeQuoteId } = rating || {};
          const isStripePayment = stripeQuoteId && isStripeAccount && stripePaymentToken;

          const payload = {
            customer,
            ratingData: rating,
            payment: isStripePayment
              ? {
                  paymentType: {
                    displayString: 'Stripe',
                    name: 'STRIPE',
                  },
                  stripe: {
                    token: stripePaymentToken,
                  },
                }
              : {
                  paymentType: {
                    displayString: 'onfile',
                    name: 'ONFILE',
                  },
                },
            ...(stripeQuoteId ? { stripeQuoteId } : null),
          };
          const response = await payForPlan(payload);

          if (response.ok) {
            if (smsPurchasedCallback) smsPurchasedCallback(smsSelection);

            if (closeOnSuccess) {
              closeDialog();
            } else {
              setSuccessfulPayment(true);
            }
          } else {
            const { errors } = await response.json();

            if (errors && errors.length === 1) {
              const [error] = errors;

              if (error.error_key === 'error.pfp.make.payment') {
                setErrorMessage(t('sms-plans.sms-payment-error', { supportPhone }));
              } else {
                setErrorMessage(error.error_message);
              }
            }
          }
        } catch (err) {
          const {
            response: { data: errMessage },
          } = err;

          if (errMessage && typeof errMessage === 'string') {
            setErrorMessage(errMessage);
          } else {
            setErrorMessage(t('sms-plans.unknown_error'));
          }
        } finally {
          setAddSmsAddon(false);
        }
      }
    })();
  }, [addSmsAddon, rating, isStripeAccount, stripePaymentToken]);

  useEffect(() => {
    if (!customer || !paymentPlanGroupId || !planId || !smsSelection || contactCount == null) return;

    const getRating = async () => {
      try {
        setLoading(true);
        setRating(null);

        const updatedRating = await getPlanRating({
          newAddOnServices: [AddOnIds.CTCT_SMS_ADDON],
          customer,
          selectedCurrency: currency,
          discount: {
            discountPeriod: overview?.paymentPeriod,
          },
          contactUsed: contactCount || 0,
          smsUsed: smsSelection.min,
          paymentPlanGroupId,
          currentPlanId: planId,
          discountCode: appliedDiscountCode,
          ...(isStripeAccount ? { stripeAccount: true } : {}),
        });

        setRating(updatedRating);

        if (appliedDiscountCode) {
          const promotionDetails = updatedRating.promotionData && updatedRating.promotionData[appliedDiscountCode];

          if (promotionDetails && !promotionDetails.applied) {
            setAppliedDiscountCode(undefined);
            setDiscountError(promotionDetails.reasonMessage);
            setDiscountAmount(0);
          } else {
            setDiscountError(undefined);

            const { promotionCredit: appliedDiscountAmount } = promotionDetails;

            setDiscountAmount(appliedDiscountAmount || 0);
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('exception getting rating', e);
      } finally {
        setLoading(false);
      }
    };

    getRating();
  }, [
    customer,
    contactCount,
    currency,
    smsSelection,
    paymentPlanGroupId,
    planId,
    appliedDiscountCode,
    overview,
    isStripeAccount,
  ]);

  useEffect(() => {
    if (currency != null && SMSTiers && SMSTiers.length) {
      const [{ price }] = SMSTiers;

      setSmsBaseCost(formatCurrency(currency, price));
    }
  }, [currency, SMSTiers]);

  const formattedPrice = useMemo(() => {
    if (smsSelection) {
      const { price } = smsSelection;

      return formatCurrency(currency, price);
    }

    return '';
  }, [currency, smsSelection]);

  if (isTrialAccount == null || isTrialAccount === true) return null;

  // TODO add actual learn more URL @61
  // TODO get planName from props or API
  return (
    <Dialog
      heading={t('sms-plans.dialog.title')}
      data-qe-id="sms-plans-dialog"
      className="sms-plans-dialog"
      contentFlush={false}
      kind="takeover"
      isOpen={open}
      onDismiss={closeDialog}
    >
      <div>
        <header className="sms-plans-header-text">
          <div className="content-left">
            <Trans i18nKey="sms-plans.dialog.subtitle-note" t={t} values={{ cost: smsBaseCost }}>
              <span style={{ fontWeight: 'bold' }} />
            </Trans>
          </div>
        </header>

        {errorMessage && (
          <div ref={alertRef}>
            <Alert kind="error" message={errorMessage} />
          </div>
        )}

        {successfulPayment && (
          <Alert
            kind="success"
            heading={t('sms-plans.sms_banner_header')}
            message={
              <Trans i18nKey="sms-plans.sms_banner_message" t={t}>
                An email was sent to {{ email: customer.email }} confirming your plan selection. Next,{' '}
                <a href={`https://app${getEnv()}constantcontact.com/pages/myaccount/settings/sms`}>register for SMS</a>{' '}
                to get your sending number.
              </Trans>
            }
            actions={
              <Button
                text={t('sms-plans.sms_banner_action')}
                onClick={() => window.open(`https://app${getEnv()}constantcontact.com/pages/myaccount/settings/sms`)}
              />
            }
          />
        )}

        <div className="sms-plans-content">
          <div className="sms-main-content">
            <div className="tiers-table-container">
              <div className="learn-more-about-sms">
                <a
                  href="https://knowledgebase.constantcontact.com/articles/KnowledgeBase/46517-Add-SMS-to-your-Constant-Contact-email-plan"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackAction({ action: 'stp-sms-learn-more-select' });
                  }}
                >
                  {t('sms-plans.learn-more')}
                </a>
              </div>
              <SMSTiersTable
                loading={loadingSMSTiers}
                currency={currency}
                selectedSMSTier={smsSelection}
                setSelectedSMSTier={(min, max, price) => {
                  trackAction({ action: 'stp-sms-tier-select' });
                  setSmsSelection({ min, max, price });
                }}
                SMSTiers={SMSTiers}
                contactCountLowEnd={contactCount}
              />
              <UpsellCard
                className="is-it-right-for-me-card"
                key="is_it_for_you"
                heading={t('sms-plans.dialog.is-sms-right-for-me')}
                description={
                  <Trans i18nKey="sms-plans.dialog.lets-double-check-a-few-things">
                    <a
                      data-qe-id="prohibited-content"
                      href="http://"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        trackAction({ action: 'stp-sms-Prohibited-Content' });
                      }}
                    >
                      terms and conditions
                    </a>
                  </Trans>
                }
                data-qe-id="is_it_for_you"
                image={<ThemeImage image="alert" data-qe-id="alert-theme-image" />}
                onClick={() => {
                  trackAction({ action: 'stp-sms-Prohibited-Content' });
                  window.open(
                    'https://knowledgebase.constantcontact.com/articles/KnowledgeBase/45188-About-Constant-Contacts-SMS-tool'
                  );
                }}
              />
            </div>
            <div className="order-summary-container">
              <OrderSummary
                data-qe-id="sms-add-on-order-summary"
                loading={loading}
                currency={currency}
                smsTierLabel={(() => {
                  if (smsSelection) {
                    return smsSelection.max === -1
                      ? t('order_summary.unlimited')
                      : `${smsSelection.min} - ${smsSelection.max}`;
                  }

                  return '';
                })()}
                rating={rating}
                discountAmount={discountAmount}
                discountError={discountError}
                validatingCode={loading}
                discountCode={discountCode}
                appliedDiscountCode={appliedDiscountCode}
                setDiscountCode={setDiscountCode}
                promoEnabled={promoEnabled}
                applyDiscountCode={(code) => setAppliedDiscountCode(code ? code.toUpperCase() : code)}
              />
            </div>
          </div>
          <div className="terms-and-conditions">
            <Trans
              i18nKey="sms-plans.dialog.authorize-recurring-charge"
              values={{ cost: formattedPrice, supportPhone }}
              t={t}
            >
              <strong />
              <a
                href="http://www.constantcontact.com/uidocs/CCSiteOwnerAgreement.jsp"
                target="_blank"
                rel="noopener noreferrer"
              >
                Constant Contact terms and condition
              </a>{' '}
            </Trans>
            <div className="sms-footer-buttons">
              <CheckBox
                name="checkbox-taxexemption"
                id="checkbox-taxexemption"
                value="TAXEXEMPTION"
                data-qe-id="agree-to-terms-checkbox"
                text={t('sms-plans.dialog.i-agree-checkbox')}
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
              />
              <div className="action-buttons">
                {successfulPayment ? (
                  <div>
                    <Button
                      block
                      className="sidebar-close-button"
                      kind="primary"
                      data-qe-id="sms-plans-dialog-close-button"
                      onClick={() => {
                        closeDialog();
                      }}
                      text={t('sms-plans.close')}
                    />
                  </div>
                ) : (
                  <>
                    <div>
                      <Button
                        block
                        className="sidebar-continue-button"
                        kind="secondary"
                        data-qe-id="sms-plans-dialog-cancel-button"
                        onClick={() => {
                          trackAction({ action: 'stp-sms-no-thanks' });
                          closeDialog();
                        }}
                        text={t('sms-plans.dialog.cancel-button')}
                      />
                    </div>
                    <div style={{ marginRight: 20 }}>
                      <Button
                        block
                        className="sidebar-continue-button"
                        kind="primary"
                        disabled={!agreeToTerms}
                        busy={addSmsAddon}
                        data-qe-id="sms-plans-dialog-continue-button"
                        onClick={() => {
                          trackAction({ action: 'stp-sms-button-select' });
                          setAddSmsAddon(true);
                        }}
                        text={t('sms-plans.add-sms')}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

SMSTierDialogWithOrderSummaryV1.defaultProps = {
  smsPurchasedCallback: null,
  closeOnSuccess: false,
};

SMSTierDialogWithOrderSummaryV1.propTypes = {
  t: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  closeDialog: PropTypes.func.isRequired,
  closeOnSuccess: PropTypes.bool,
  smsPurchasedCallback: PropTypes.func,
};

export default withTranslation()(SMSTierDialogWithOrderSummaryV1);
